import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../../constants/routes';
import { UI, DISPLAY, EASE, h2, SECTION_GAP } from '../../styles/tl5';
import { useListDoctorsQuery } from '../../store/siteContentApi';
import { useListTestimonialsQuery } from '../../store/testimonialsApi';
import { useGetPageSectionsQuery } from '../../store/siteContentApi';

// "Technology runs the process. Doctors make the call." — section #trust.
//
// One section holding three equal cards. We previously shipped these as
// three separate full-width sections (AiBand, DoctorsStrip, Reviews); the
// reference packs them into a single three-column band, which is why
// those components are no longer rendered on the home page. They are left
// in the tree untouched in case the longer treatments are wanted again.
//
// The quality list is static copy. The doctors and the reviews come from
// the same endpoints the old sections used, so nothing that the admin
// panel controls is lost by the merge.

// Fallback — mirrors DEFAULTS.home.why_trust in pageSectionController.
const QUALITY = [
  {
    t: 'Sample checks',
    s: 'Clotting or haemolysis caught before testing, so fewer repeat draws.',
  },
  {
    t: 'Result cross-checks',
    s: 'Values compared with your past results. Anything unusual goes to a pathologist.',
  },
  {
    t: 'Critical alerts',
    s: 'Dangerous values flagged at once, so a doctor can call you quickly.',
  },
  {
    t: 'Every step measured',
    s: 'Turnaround and errors tracked daily. We fix the cause, not just the case.',
  },
];

// Shown only until the admin has published real doctors / testimonials,
// so the band never collapses to one card on a fresh install.
const FALLBACK_DOCTORS = [
  { name: 'Dr. [Name]', qualifications: 'MD Pathology · Lab Director' },
  { name: 'Dr. [Name]', qualifications: 'MD Biochemistry' },
  { name: 'Dr. [Name]', qualifications: 'MD Microbiology' },
  { name: 'Dr. [Name]', qualifications: 'PhD Genetics' },
];

const FALLBACK_REVIEWS = [
  {
    quote: 'The Trustian came at 7 am sharp, was gentle with my father and the report was on WhatsApp by lunch.',
    name: '[Name]',
    city: 'Hyderabad',
  },
  {
    quote: 'One value was high. A TrustLab doctor called me the same evening and explained what to ask my physician.',
    name: '[Name]',
    city: 'Vijayawada',
  },
];

const cardBase = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  p: 'clamp(22px, 3vw, 30px)',
  borderRadius: '28px',
};

const cardTitle = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.15,
};

const cardLink = {
  mt: 'auto',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  transition: `gap .3s ${EASE}`,
  '&:hover': { gap: '12px' },
};

const Stars = ({ size = 14, spacing = 2 }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ color: 'var(--tl-gold)', fontSize: size, letterSpacing: `${spacing}px`, lineHeight: 1 }}
  >
    ★★★★★
  </Box>
);

const WhyTrust = () => {
  const { data: apiDoctors = [] } = useListDoctorsQuery();
  const { data: apiReviews = [] } = useListTestimonialsQuery();
  const { data: sections } = useGetPageSectionsQuery('home');

  const c = sections?.why_trust || {};
  const quality = c.quality?.length ? c.quality : QUALITY;
  const qualityHeading = c.quality_heading || 'The same right answer, every time';
  const doctorsHeading = c.doctors_heading || 'The doctors behind your report';
  const reviewsHeading = c.reviews_heading || 'What patients say';

  const doctors = (apiDoctors.length ? apiDoctors : FALLBACK_DOCTORS).slice(0, 4);
  const reviews = (apiReviews.length ? apiReviews : FALLBACK_REVIEWS).slice(0, 2);

  return (
    <Box
      component="section"
      id="trust"
      sx={{
        width: '100%',
        maxWidth: 1320,
        mx: 'auto',
        pt: SECTION_GAP,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1, maxWidth: 720 }}>
        <Box
          component="span"
          sx={{
            fontFamily: UI,
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--tl-teal-d)',
          }}
        >
          Why trust TrustLab
        </Box>
        <Typography component="h2" sx={h2}>
          Technology runs the process. Doctors make the call.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        {/* ── 1. the quality list, on dark ──────────────────────────── */}
        <Box sx={{ ...cardBase, gap: 1, bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
          <Box component="span" sx={cardTitle}>{qualityHeading}</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {quality.map((q) => (
              <Box
                key={q.t}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                  py: 1.5,
                  borderBottom: '1px solid var(--tl-g6)',
                }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800, color: '#FFFFFF' }}>
                  {q.t}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-line-2)' }}>
                  {q.s}
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            component={Link}
            to={`${ROUTES.ABOUT}#quality`}
            sx={{ ...cardLink, pt: 1.5, color: 'var(--tl-yellow)' }}
          >
            How we ensure quality →
          </Box>
        </Box>

        {/* ── 2. the doctors ────────────────────────────────────────── */}
        <Box sx={{ ...cardBase, gap: 2, bgcolor: '#FFFFFF' }}>
          <Box component="span" sx={cardTitle}>{doctorsHeading}</Box>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted-2)' }}>
            Our pathologists verify every result before it reaches you.
          </Box>
          {doctors.map((d, i) => (
            <Box key={d._id || `${d.name}-${i}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Box
                component="span"
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: 'var(--tl-line)',
                  overflow: 'hidden',
                  display: 'block',
                }}
              >
                {d.photo_url && (
                  <Box
                    component="img"
                    src={d.photo_url}
                    alt={d.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>
                  {d.name}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                  {d.title || d.qualifications}
                </Box>
              </Box>
            </Box>
          ))}
          <Box component={Link} to={ROUTES.DOCTORS} sx={{ ...cardLink, color: 'var(--tl-teal-d)' }}>
            Meet the team →
          </Box>
        </Box>

        {/* ── 3. the reviews ────────────────────────────────────────── */}
        <Box sx={{ ...cardBase, gap: 1.75, bgcolor: '#FFFFFF' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box component="span" sx={cardTitle}>{reviewsHeading}</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22 }}>4.8</Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Stars size={12} spacing={1} />
                <Box component="span" sx={{ fontFamily: UI, fontSize: 11, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                  Google reviews
                </Box>
              </Box>
            </Box>
          </Box>

          {reviews.map((r, i) => (
            <Box
              key={r._id || `${r.name}-${i}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                borderRadius: '18px',
                bgcolor: 'var(--tl-mint)',
              }}
            >
              <Stars />
              <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55 }}>
                {r.quote || r.text}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 12, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                {[r.name, r.city].filter(Boolean).join(' · ')}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WhyTrust;
